import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { useGPSummary } from "../../hooks/queries/useGPSummary";
import { useAuth } from "../../context/AuthContext";
import { generateGPSummaryPDF } from "../../services/pdf";
import type { PDFSectionToggles } from "../../services/pdf";
import PatientInfoCard from "./components/PatientInfoCard";
import SnapshotCard from "./components/SnapshotCard";
import MoodTrendsCard from "./components/MoodTrendsCard";
import KeySymptomsCard from "./components/KeySymptomsCard";
import RedFlagHistoryCard from "./components/RedFlagHistoryCard";
import VaginalBleedingCard from "./components/VaginalBleedingCard";
import ActivitiesCard from "./components/ActivitiesCard";
import JournalThemesCard from "./components/JournalThemesCard";
import NotesCard from "./components/NotesCard";
import ExportCard from "./components/ExportCard";
import GPSummaryLoading from "./components/GPSummaryLoading";
import GPSummaryError from "./components/GPSummaryError";

export default function GPSummaryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading, isError, refetch } = useGPSummary();

  const [notes, setNotes] = useState("");
  const [toggles, setToggles] = useState<PDFSectionToggles>({
    patientInfo: true,
    snapshot: true,
    mood: true,
    symptoms: true,
    bleeding: true,
    journal: false,
    activities: true,
    redFlags: true,
  });

  const flip = (key: string) => setToggles((t) => ({ ...t, [key]: !t[key as keyof PDFSectionToggles] }));

  const handleExportPDF = useCallback(async () => {
    if (!data || !user) return;

    await generateGPSummaryPDF({
      summaryData: data,
      toggles,
      notes,
      uid: user.uid,
    });
  }, [data, user, toggles, notes]);

  return (
    <AppShell
      headerConfig={{
        title: "Recovery Summary",
        subtitle: data?.meta.dateRange ?? "",
        gradient: "linear-gradient(167.14deg, #6C7A52 -2.38%, #C6D8A5 94.45%)",
        height: 140,
        headerActions: (
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm font-medium text-white/80 hover:text-white transition-colors"
          >
            {"\u2190"} Back
          </button>
        ),
      }}
    >
      {isLoading && <GPSummaryLoading />}

      {isError && <GPSummaryError onRetry={() => refetch()} />}

      {data && (
        <div className="px-4 pt-8 grid grid-cols-1 gap-4">
          <PatientInfoCard userInfo={data.userInfo} meta={data.meta} />
          <SnapshotCard snapshot={data.snapshot} />
          <MoodTrendsCard mood={data.mood} avg={data.moodAverage} trendText={data.moodTrendText} />
          <KeySymptomsCard symptoms={data.symptoms} />
          <RedFlagHistoryCard redFlags={data.redFlags} />
          <ActivitiesCard
            weeklyActivities={data.weeklyActivities}
            completedWeek={data.snapshot.completedWeek}
          />
          <VaginalBleedingCard bleedingLog={data.bleedingLog} />
          <JournalThemesCard themes={data.journalThemes} />
          <NotesCard notes={notes} onChange={setNotes} />
          <ExportCard toggles={toggles as unknown as Record<string, boolean>} onToggle={flip} onExportPDF={handleExportPDF} />
        </div>
      )}
    </AppShell>
  );
}
